import React from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import BigTitle from '../subComponents/BigTitlte'
import PixelSprite from '../pixel/PixelSprites'
import { Skills } from '../data/SkillData'
import characterImg from '../assets/Images/character.png'
import { PixelPanel, pixelCorners, pixelShadow } from '../pixel/pixelUI'

const STATS = [
  { label: 'FRONTEND', value: 92, color: '#FF3E8E' },
  { label: 'MOBILE', value: 88, color: '#4FC9FF' },
  { label: 'BACKEND', value: 78, color: '#8CE04A' },
  { label: 'CREATIVE CODE', value: 84, color: '#C9A6FF' },
]

const Box = styled.main`
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 8rem 2rem 9rem;
  background:
    linear-gradient(${(props) => props.theme.buttercup}18 2px, transparent 2px),
    linear-gradient(90deg, ${(props) => props.theme.buttercup}18 2px, transparent 2px),
    ${(props) => props.theme.body};
  background-size: 28px 28px;

  @media (max-width: 600px) {
    padding: 11rem 1rem 4rem;
  }
`

const PageWrap = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
`

const Intro = styled(motion.header)`
  margin-bottom: 2.3rem;
`

const Kicker = styled.p`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.58rem;
  color: ${(props) => props.theme.buttercup};
  margin-bottom: 0.8rem;
`

const PageTitle = styled.h2`
  font-size: clamp(1.45rem, 4vw, 3rem);
  color: ${(props) => props.theme.black};
  margin-bottom: 0.7rem;
`

const IntroText = styled.p`
  max-width: 62ch;
  font-size: 1.05rem;
  line-height: 1.6;
  font-weight: 600;
`

const CharacterSheet = styled(PixelPanel)`
  display: grid;
  grid-template-columns: minmax(260px, 0.72fr) minmax(0, 1.28fr);
  overflow: hidden;
  margin-bottom: 2.8rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

const AvatarSide = styled.div`
  min-height: 25rem;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  padding-top: 4rem;
  background:
    radial-gradient(circle at 18% 20%, #fff 0 5px, transparent 6px),
    radial-gradient(circle at 82% 30%, #fff 0 4px, transparent 5px),
    linear-gradient(155deg, ${(props) => props.theme.bubbles}, ${(props) => props.theme.lavender});
  border-right: 4px solid ${(props) => props.theme.black};

  img {
    width: min(95%, 24rem);
    object-fit: contain;
  }

  @media (max-width: 760px) {
    min-height: 20rem;
    border-right: 0;
    border-bottom: 4px solid ${(props) => props.theme.black};
  }
`

const ClassBadge = styled.div`
  position: absolute;
  left: 1rem;
  top: 1rem;
  background: ${(props) => props.theme.yellow};
  border: 3px solid ${(props) => props.theme.black};
  padding: 0.7rem 0.8rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.5rem;
  line-height: 1.7;
  ${pixelCorners(5)}
  ${(props) => pixelShadow(4, 4)}

  span { color: ${(props) => props.theme.blossom}; }
`

const SheetBody = styled.div`
  padding: clamp(1.5rem, 4vw, 2.5rem);
  background: #fff;
`

const SheetTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.35rem;
  margin-bottom: 1.5rem;
  border-bottom: 3px dashed ${(props) => props.theme.black};
`

const PlayerName = styled.h3`
  font-size: clamp(1.2rem, 2.4vw, 1.9rem);
  margin-bottom: 0.55rem;
`

const PlayerClass = styled.p`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.52rem;
  color: ${(props) => props.theme.blossom};
`

const ReadyBadge = styled.span`
  flex: 0 0 auto;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.48rem;
  color: ${(props) => props.theme.black};
  background: ${(props) => props.theme.buttercup};
  border: 2px solid ${(props) => props.theme.black};
  padding: 0.55rem;
`

const StatList = styled.div`
  display: grid;
  gap: 1.15rem;
`

const StatRow = styled.div``

const StatLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.45rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.5rem;

  span:last-child { color: ${(props) => props.$color}; }
`

const StatTrack = styled.div`
  height: 1rem;
  padding: 2px;
  border: 3px solid ${(props) => props.theme.black};
  background: ${(props) => props.theme.body};
`

const StatFill = styled(motion.div)`
  height: 100%;
  background: repeating-linear-gradient(
    90deg,
    ${(props) => props.$color} 0 14px,
    ${(props) => props.$color}cc 14px 18px
  );
`

const InventoryHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.3rem;

  h3 { font-size: clamp(1rem, 2.5vw, 1.5rem); }

  span {
    font-family: ${(props) => props.theme.pixelFont};
    font-size: 0.48rem;
    color: ${(props) => props.theme.blossom};
  }
`

const InventoryGrid = styled(motion.section)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.4rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const CategoryCard = styled(motion.article)`
  position: relative;
  overflow: hidden;
  background: #fff;
  border: 4px solid ${(props) => props.theme.black};
  padding: 1.25rem;
  ${pixelCorners(8)}
  ${(props) => pixelShadow(6, 6, props.$accent)}

  &:last-child:nth-child(odd) {
    grid-column: 1 / -1;
    width: calc(50% - 0.7rem);
    justify-self: center;
  }

  @media (max-width: 700px) {
    &:last-child:nth-child(odd) {
      grid-column: auto;
      width: 100%;
    }
  }
`

const CategoryHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin: -1.25rem -1.25rem 1.1rem;
  padding: 0.9rem 1rem;
  background: ${(props) => props.$accent};
  border-bottom: 4px solid ${(props) => props.theme.black};

  h4 {
    font-size: clamp(0.75rem, 1.5vw, 1rem);
    color: ${(props) => props.theme.black};
  }
`

const ChipGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`

const Chip = styled(motion.span)`
  font-weight: 750;
  font-size: 0.9rem;
  background: ${(props) => props.theme.body};
  border: 2px solid ${(props) => props.theme.black};
  padding: 0.55rem 0.7rem;
  box-shadow: 3px 3px 0 ${(props) => props.$accent};
`

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
}

const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 240, damping: 22 } },
}

const MySkillsPage = () => (
  <Box>
    <BigTitle text="SKILL" top="4%" right="2%" />
    <PageWrap>
      <Intro initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <Kicker><PixelSprite name="star" size={3} color="#8CE04A" /></Kicker>
        <PageTitle>Character Stats</PageTitle>
        <IntroText>
          A practical toolkit for shipping products across web, mobile, backend systems,
          and creative interactive experiences.
        </IntroText>
      </Intro>

      <CharacterSheet>
        <AvatarSide>
          <ClassBadge><span>CLASS</span><br />SOFTWARE ENGINEER</ClassBadge>
          <img src={characterImg} alt="Pixel-art portrait of Hermia" />
        </AvatarSide>
        <SheetBody>
          <SheetTop>
            <div>
              <PlayerName>Hermia Tian</PlayerName>
              <PlayerClass>PRODUCT BUILDER · CREATIVE CODER</PlayerClass>
            </div>
            <ReadyBadge>READY</ReadyBadge>
          </SheetTop>
          <StatList>
            {STATS.map((stat, index) => (
              <StatRow key={stat.label}>
                <StatLabel $color={stat.color}><span>{stat.label}</span><span>FOCUS</span></StatLabel>
                <StatTrack>
                  <StatFill
                    $color={stat.color}
                    initial={{ width: 0 }}
                    animate={{ width: `${stat.value}%` }}
                    transition={{ duration: 0.7, delay: 0.15 + index * 0.12 }}
                  />
                </StatTrack>
              </StatRow>
            ))}
          </StatList>
        </SheetBody>
      </CharacterSheet>

      <InventoryHeading>
        <h3>Equipped Skills</h3>
        <span>23 ITEMS UNLOCKED</span>
      </InventoryHeading>
      <InventoryGrid variants={container} initial="hidden" animate="show">
        {Skills.map((category) => (
          <CategoryCard key={category.id} $accent={category.accent} variants={item}>
            <CategoryHead $accent={category.accent}>
              <PixelSprite name="sparkle" size={3} color="#1A1420" />
              <h4>{category.title}</h4>
            </CategoryHead>
            <ChipGrid>
              {category.items.map((skill) => (
                <Chip key={skill} $accent={category.accent} whileHover={{ y: -3, rotate: -1 }}>
                  {skill}
                </Chip>
              ))}
            </ChipGrid>
          </CategoryCard>
        ))}
      </InventoryGrid>
    </PageWrap>
  </Box>
)

export default MySkillsPage
